import { LEDModes } from "@aura-frontend/omnibus-az-client";
import {
  DatePicker,
  Dialog,
  generateTimeOptions,
  Label,
  TimePicker,
} from "@win-reality/react-ui";
import { LoaderCircle } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { MachineOrganizationSelect } from "@/components/MachineOrganizationSelect/MachineOrganizationSelect";
import { useOwnerOrgsFromMachine } from "@/hooks/useOwnerOrgsFromMachine";
import { formatHHMM, isSameDay } from "@/utils/dateUtils";

type EnableTarget = Exclude<LEDModes, never>;

const TITLES: Record<EnableTarget, string> = {
  stat_tracking: "Enabling Stat Tracking & ABS Challenge System",
  umpire_assist: "Enabling Automatic Umpire",
  blocked: "Enabling Blocked",
};

const DESCRIPTIONS: Record<EnableTarget, string> = {
  stat_tracking:
    "Pick the date and time when Stat Tracking & ABS Challenge System should end.",
  umpire_assist:
    "Pick the date and time when stat tracking and the automatic umpire should end.",
  blocked:
    "Pick the date and time when the LED board should stop being blocked.",
};

type EnableLEDBoardDialogProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  targetMode: EnableTarget | null;
  // When provided, the dialog opens with this date+time preselected — used when
  // the user is reviewing the existing schedule for the committed mode.
  initialEndTimeISO?: string | null;
  machineUuid: string;
  onConfirm: (endTimeISO: string, orgId: string | null) => void;
  isSubmitting: boolean;
};

// Snap (now + 5min) UP to the next 15-minute slot. Same heuristic as the
// Overview event form so the earliest allowed pickable slot is itself a
// valid "HH:MM" option, enabling unambiguous string comparison.
function earliestSlotToday(): string {
  const cutoff = new Date();
  cutoff.setSeconds(0, 0);
  cutoff.setMinutes(cutoff.getMinutes() + 5);
  const remainder = cutoff.getMinutes() % 15;
  if (remainder > 0) {
    cutoff.setMinutes(cutoff.getMinutes() + (15 - remainder));
  }
  return formatHHMM(cutoff);
}

function EnableLEDBoardDialog({
  isOpen,
  onOpenChange,
  targetMode,
  initialEndTimeISO,
  machineUuid,
  onConfirm,
  isSubmitting,
}: EnableLEDBoardDialogProps) {
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const [startDate, setStartDate] = useState<Date | undefined>(today);
  const [endTimeStr, setEndTimeStr] = useState<string>("");
  const [orgId, setOrgId] = useState<string | null>(null);

  const {
    orgs,
    count,
    isLoading: isLoadingOrgs,
  } = useOwnerOrgsFromMachine({
    machineUuid,
    enabled: isOpen,
  });

  useEffect(() => {
    if (!isOpen) return;
    if (initialEndTimeISO) {
      const initial = new Date(initialEndTimeISO);
      const day = new Date(initial);
      day.setHours(0, 0, 0, 0);
      setStartDate(day);
      setEndTimeStr(formatHHMM(initial));
      setOrgId(null);
      return;
    }
    setStartDate(today);
    setEndTimeStr("");
    setOrgId(null);
  }, [isOpen, today, initialEndTimeISO]);

  const endTimeOptions = useMemo(() => {
    const options = generateTimeOptions("15m");
    if (!startDate || !isSameDay(startDate, new Date())) return options;
    const earliest = earliestSlotToday();
    return options.filter((opt) => opt.value >= earliest);
  }, [startDate]);

  // If the user flips the date back to today and the previously-picked time
  // is now in the past, clear it so they have to re-pick a valid slot.
  useEffect(() => {
    if (!startDate || !endTimeStr) return;
    if (!isSameDay(startDate, new Date())) return;
    if (endTimeStr < earliestSlotToday()) setEndTimeStr("");
  }, [startDate, endTimeStr]);

  // Auto-select the org if there's exactly one and none is selected yet.
  useEffect(() => {
    if (!isOpen || orgId !== null) return;
    if (count === 1) setOrgId(orgs[0].orgId);
  }, [isOpen, count, orgs, orgId]);

  const combinedISO = useMemo(() => {
    if (!startDate || !endTimeStr) return null;
    const [h, m] = endTimeStr.split(":").map(Number);
    const combined = new Date(startDate);
    combined.setHours(h, m, 0, 0);
    return combined.toISOString();
  }, [startDate, endTimeStr]);

  const valid = !!combinedISO && (count === 0 || !!orgId);
  const title = targetMode ? TITLES[targetMode] : "";
  const description = targetMode ? DESCRIPTIONS[targetMode] : undefined;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={onOpenChange}
      size="md"
      hideXButton
      title={title}
      primaryAction={{
        label: "Confirm",
        isDisabled: !valid || isSubmitting,
        isLoading: isSubmitting,
        onClick: () => {
          if (combinedISO) onConfirm(combinedISO, orgId);
        },
      }}
    >
      {isLoadingOrgs ? (
        <div className="flex items-center justify-center py-8">
          <LoaderCircle className="h-6 w-6 animate-spin text-gray-500" />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label className="text-sm mb-0">
              Organization
              <span className="text-red-500">{count > 1 ? "*" : ""}</span>
            </Label>
            <MachineOrganizationSelect
              orgs={orgs}
              value={orgId}
              onChange={setOrgId}
              isLoading={isLoadingOrgs}
            />
          </div>

          {description && (
            <p className="text-sm text-gray-600">{description}</p>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <Label className="text-sm mb-0">
                End Date<span className="text-red-500">*</span>
              </Label>
              <DatePicker
                value={startDate}
                onValueChange={setStartDate}
                disabled={{ before: today }}
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label className="text-sm mb-0">
                End Time<span className="text-red-500">*</span>
              </Label>
              <TimePicker
                value={endTimeStr || undefined}
                onValueChange={(v) => setEndTimeStr(v ?? "")}
                options={endTimeOptions}
                placeholder="Select time"
                disabled={!startDate}
              />
            </div>
          </div>
        </div>
      )}
    </Dialog>
  );
}

export { EnableLEDBoardDialog };
